import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  EmailLogAttachment,
  EmailLogDetail,
  EmailMessageEventType,
  EmailMessageStatus,
  emailLogState,
  isEmailLogStateProblem,
} from '@models/email-logs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmailLogResendService } from '@services/email-log-resend.service';
import { EmailLogsService } from '@services/email-logs.service';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { map } from 'rxjs';

const EMAIL_LOG_LIST_URL = '/email-logs';

/**
 * Badge colours per state name. One map covers both axes (`Status` and `DeliveryStatus`) plus the two
 * event-only types: the names don't collide except `Failed`, which is bad news on either axis.
 * `SoftBounced` is amber rather than red — the provider may still deliver after a soft bounce.
 */
const STATE_CLASSES: Record<string, string> = {
  // Pipeline states
  Pending: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  Sending: 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-200',
  Accepted: 'bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-200',
  Cancelled: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  // Delivery states
  Sent: 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-200',
  Delivered: 'bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-200',
  SoftBounced: 'bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200',
  HardBounced: 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-200',
  SpamComplaint: 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-200',
  Suppressed: 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-200',
  PolicyRejected: 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-200',
  Failed: 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-200',
  Unsubscribed: 'bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200',
  // Event-only types (never promoted to a delivery status)
  Opened: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-200',
  Clicked: 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-200',
};

const NEUTRAL_STATE_CLASSES = 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';

/** Material icon per timeline event type. */
const EVENT_ICONS: Record<EmailMessageEventType, string> = {
  Sent: 'send',
  Delivered: 'mark_email_read',
  SoftBounced: 'history',
  HardBounced: 'error',
  SpamComplaint: 'report',
  Suppressed: 'block',
  PolicyRejected: 'gavel',
  Failed: 'error',
  Unsubscribed: 'unsubscribe',
  Opened: 'drafts',
  Clicked: 'ads_click',
};

/** Statuses for which `nextAttemptAt` is still meaningful (retry moment / lease expiry). */
const PENDING_STATUSES: readonly EmailMessageStatus[] = [EmailMessageStatus.Pending, EmailMessageStatus.Sending];

@Component({
  selector: 'app-email-log-detail',
  templateUrl: './email-log-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    RouterLink,
    TranslatePipe,
  ],
})
export class EmailLogDetailComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly emailLogsService = inject(EmailLogsService);
  private readonly toast = inject(ToastService);
  private readonly resendService = inject(EmailLogResendService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly translate = inject(TranslateService);

  // Read from the param map rather than the snapshot: the "view queued copy" / "view original" links
  // navigate to another id on this same route, so the component instance is reused.
  private readonly routeId = toSignal(this.route.paramMap.pipe(map((params) => Number(params.get('id')) || null)), {
    initialValue: null,
  });

  private readonly backUrl = toSignal(
    this.route.queryParamMap.pipe(map((params) => params.get('backUrl') ?? EMAIL_LOG_LIST_URL)),
    { initialValue: EMAIL_LOG_LIST_URL },
  );

  readonly detailResource = rxResource({
    params: () => {
      const id = this.routeId();
      return id ? { id } : undefined;
    },
    stream: ({ params }) =>
      this.emailLogsService.getEmailLog(params.id).pipe(showGenericErrorToast(this.toast, () => undefined)),
  });

  readonly detail = computed<EmailLogDetail | null>(() => this.detailResource.value() ?? null);

  /** Full-page spinner while there is nothing to show yet. */
  readonly initialLoading = computed(() => this.detailResource.isLoading() && this.detail() === null);
  /**
   * Nothing to show: a bad route id, or a settled request that produced no value (the error was
   * already toasted by {@link showGenericErrorToast}). Deliberately does NOT fire while the resource
   * is still idle/loading, so the empty state can't flash before the first fetch starts.
   */
  readonly loadFailed = computed(() => {
    if (this.routeId() === null) return true;
    const status = this.detailResource.status();
    return this.detail() === null && (status === 'resolved' || status === 'local' || status === 'error');
  });

  readonly resending = signal(false);
  readonly downloadingAttachmentId = signal<number | null>(null);

  /**
   * A message still in the outbox pipeline can't be resent — the API rejects it with a 400 ("still
   * queued for sending"), since the original send hasn't happened yet.
   */
  readonly canResend = computed(() => {
    const log = this.detail();
    return log !== null && !PENDING_STATUSES.includes(log.status);
  });

  /** Coalesced state (`deliveryStatus ?? status`) — only used to flag the message as problematic. */
  readonly isProblem = computed(() => {
    const log = this.detail();
    return log !== null && isEmailLogStateProblem(emailLogState(log));
  });

  readonly showNextAttempt = computed(() => {
    const log = this.detail();
    return !!log?.nextAttemptAt && PENDING_STATUSES.includes(log.status);
  });

  // Body view: HTML by default, plain text on request when the message stored an alternative. The
  // effective view falls back to HTML so the toggle can't strand a message without a text body.
  private readonly bodyViewPreference = signal<'html' | 'text'>('html');
  readonly bodyView = computed<'html' | 'text'>(() =>
    this.bodyViewPreference() === 'text' && this.detail()?.textBody ? 'text' : 'html',
  );

  readonly showTemplateData = signal(false);

  /** Caller-supplied template metadata, pretty-printed when it parses as JSON. */
  readonly prettyTemplateData = computed<string | null>(() => {
    const raw = this.detail()?.templateData;
    if (!raw) return null;
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  });

  // The stored body is rendered inside a sandboxed <iframe srcdoc> (no allow-scripts), so it is isolated
  // from the admin app. We bypass Angular's HTML sanitizer to keep the email's original markup/styles
  // intact — the iframe sandbox is the security boundary.
  readonly safeBody = computed<SafeHtml | null>(() => {
    const body = this.detail()?.body;
    return body ? this.sanitizer.bypassSecurityTrustHtml(body) : null;
  });

  // ── Navigation ────────────────────────────────────────────

  navigateBack(): void {
    this.router.navigateByUrl(this.backUrl());
  }

  /** Route for another log entry (resend original / queued copy); keeps the same back target. */
  emailLogLink(id: number): (string | number)[] {
    return [EMAIL_LOG_LIST_URL, id];
  }

  readonly linkQueryParams = computed(() => ({ backUrl: this.backUrl() }));

  // ── Actions ───────────────────────────────────────────────

  /**
   * Queues a copy for the recipient SNAPSHOT stored on this row (never the customer's current
   * address). The API answers 202, so the copy is queued, not sent — on success we navigate straight
   * to the new row (the thing whose progress the user now cares about); it carries a "resend of"
   * back-link to this one, so nothing is lost.
   */
  async resend(): Promise<void> {
    const log = this.detail();
    if (!log) return;

    const result = await this.resendService.confirmAndResend(log.id, log.recipientEmail, () =>
      this.resending.set(true),
    );
    this.resending.set(false);
    if (!result) return;

    this.router.navigate(this.emailLogLink(result.id), { queryParams: this.linkQueryParams() });
  }

  downloadAttachment(attachment: EmailLogAttachment): void {
    const log = this.detail();
    if (!log) return;

    // Goes through HttpClient (not a plain link) because the endpoint needs the bearer token.
    this.downloadingAttachmentId.set(attachment.id);
    this.emailLogsService.saveAttachment(log.id, attachment.id, attachment.fileName).subscribe({
      next: () => this.downloadingAttachmentId.set(null),
      error: (err) => {
        this.downloadingAttachmentId.set(null);
        this.toast.showError(err);
      },
    });
  }

  toggleBodyView(view: 'html' | 'text'): void {
    this.bodyViewPreference.set(view);
  }

  toggleTemplateData(): void {
    this.showTemplateData.update((v) => !v);
  }

  // ── Display helpers ───────────────────────────────────────

  /** Badge colours for a pipeline state, delivery state or event type. */
  stateClass(state: string | null): string {
    return (state && STATE_CLASSES[state]) || NEUTRAL_STATE_CLASSES;
  }

  /**
   * ngx-translate echoes the key back when a translation is missing; degrade to the raw enum name so a
   * backend that grew a new log type / status / event type reads sensibly instead of showing an i18n key
   * (the enum value lists are owned by the API and grow as CS-216 senders land).
   */
  enumLabel(prefix: 'logType' | 'status' | 'deliveryStatus' | 'eventType', raw: string): string {
    const key = `email-logs.${prefix}.${raw}`;
    const translated: string = this.translate.instant(key);
    return translated === key ? raw : translated;
  }

  eventIcon(eventType: EmailMessageEventType): string {
    return EVENT_ICONS[eventType] ?? 'mail_outline';
  }

  /** Human-readable attachment size; the API reports raw bytes. */
  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  }
}
